import "./NewYear.css";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import config from "../config";

const serverurl = config.apiUrl;

export default function NewYear() {
  // LIVE COUNT STATES
  const [postCount, setPostCount] = useState(0);
  const [animatedCount, setAnimatedCount] = useState(0);

  // Refs for animation control and cleanup
  const rafRef = useRef(null);
  const startValueRef = useRef(0);
  const startTimeRef = useRef(null);
  const targetValueRef = useRef(0);
  const isMountedRef = useRef(true);

  // FETCH COUNT FROM BACKEND (initial + periodic)
  useEffect(() => {
    isMountedRef.current = true;

    const getCount = async () => {
      try {
        const { data } = await axios.get(`${serverurl}/api/v1/getpostcount`);
        const count = Number(data.count) || 0;
        if (isMountedRef.current) setPostCount(count);
      } catch (_) {
      }
    };

    getCount();
    const interval = setInterval(getCount, 10000); // auto refresh every 10 sec

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ANIMATION EFFECT: animate from current displayed value to latest postCount
  useEffect(() => {
    // cancel any running frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const from = Number(animatedCount) || 0; // start from currently displayed value
    const to = Number(postCount) || 0;

    // nothing to animate
    if (from === to) return;

    startValueRef.current = from;
    targetValueRef.current = to;
    startTimeRef.current = null;

    // Make animation duration scale with delta but keep it bounded
    const delta = Math.abs(to - from);
    const duration = Math.min(1400, Math.max(400, Math.floor(400 + delta * 0.5)));

    // easing: easeOutCubic
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      const current = Math.floor(startValueRef.current + (to - startValueRef.current) * eased);
      setAnimatedCount(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
        // make sure final value is exact
        setAnimatedCount(to);
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postCount]);

  return (
    <div className="home-wrapper">

      {/* HERO SECTION */}
      <section className="hero-section">

        <h1 className="hero-title">
          Ready to start Your Best Year Yet?
        </h1>

        <p className="hero-subtitle">
          Set your goals with intention. Track your journey.
          Reveal your growth next January.
        </p>

        {/* LIVE RESOLUTION COUNT */}
        <div className="resolution-count">
          <span className="resolution-number">
            {animatedCount.toLocaleString()}
          </span>
          <span className="resolution-label">
            resolutions are already created!
          </span>
        </div>

        <div className="hero-buttons">
          <a href="/login" className="btn-primary">
            Start Your Journey
          </a>
        </div>
      </section>

      {/* MOTTO SECTION */}
      <section className="motto-section">
        <div className="motto-container">
          <h2 className="motto-heading">What This Is</h2>
          <p className="motto-text">
            This is a quiet space to set new your year resolutions without the noise. You drop your intentions
            here, anonymously, and walk through the year at your own pace. No likes,
            no pressure, no competition, just personal growth. When the new year
            arrives, your journey unfolds and you get to see how far you’ve really come.
          </p>
        </div>
      </section>

      {/* SECOND SECTION */}
      <section className="info-section">
        <div className="info-grid">

          <div className="info-card">
            <h3>Define Your Direction</h3>
            <p>
              Real change begins with clarity. Set goals that truly push your potential.
            </p>
          </div>

          <div className="info-card">
            <h3>Stay Accountable</h3>
            <p>
              Update your progress throughout the year and keep your resolutions on track.
            </p>
          </div>

          <div className="info-card">
            <h3>Celebrate Your Growth</h3>
            <p>
              When January arrives, see the full picture of your personal evolution as your record releases.
            </p>
          </div>

        </div>
      </section>

    </div>
  );
}
